import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import ClampVisualizer from '@/components/help/widgets/ClampVisualizer'
import RateStabilityVisualizer from '@/components/help/widgets/RateStabilityVisualizer'
import { Ruler, TrendingDown, Gauge } from 'lucide-react'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function PriceGuardrails({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Price Guardrails"
      subtitle="Three independent safety nets — hard room bounds, an adaptive floor, and a speed limit on change."
    >
      <GuideSection title="Why Guardrails?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The rate pipeline applies multiple adjustments that can compound. A negative season
          modifier combined with a negative demand signal could push a rate uncomfortably low; a
          demand spike during a peak-season event could push it higher than your market will bear.
          Guardrails are the boundaries of your pricing strategy: inside them the engine optimises
          freely, at the edges it stops and holds.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {brand} gives you <strong className="text-foreground">three</strong> guardrails. They solve
          different problems and work independently — you can run any one alone, or stack them. The
          most common confusion is between the Dynamic Floor and Rate Stability, so this guide draws
          that line clearly.
        </p>
      </GuideSection>

      <GuideSection title="The Three Guardrails at a Glance">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border/60 bg-white p-4 shadow-sm">
            <Ruler className="h-5 w-5 text-slate-500" />
            <h4 className="mt-2 text-sm font-semibold text-foreground">Room Bounds</h4>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              A fixed floor and ceiling per room type. Hard absolute limits you set by hand. The
              rate can never leave this band on the room's lead plan (except a manual override);
              other plans keep their offset from it, so one can sit below the floor by that offset.
            </p>
            <p className="mt-2 text-[11px] font-medium text-slate-600">
              Controls: the absolute price range
            </p>
          </div>
          <div className="rounded-xl border border-border/60 bg-white p-4 shadow-sm">
            <TrendingDown className="h-5 w-5 text-primary" />
            <h4 className="mt-2 text-sm font-semibold text-foreground">Dynamic Floor</h4>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              An adaptive minimum that rises and falls with your recent performance — a percentage
              of your 30-day average rate. Protects against race-to-the-bottom discounting.
            </p>
            <p className="mt-2 text-[11px] font-medium text-primary">
              Controls: how low a rate can go
            </p>
          </div>
          <div className="rounded-xl border border-border/60 bg-white p-4 shadow-sm">
            <Gauge className="h-5 w-5 text-amber-500" />
            <h4 className="mt-2 text-sm font-semibold text-foreground">Rate Stability</h4>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              A speed limit on change. Caps how far the rate can move from the previously
              published rate in a single cycle — separate caps for increases and decreases.
            </p>
            <p className="mt-2 text-[11px] font-medium text-amber-600">
              Controls: how fast a rate can change
            </p>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Read those last lines together: Room Bounds set the <em>range</em>, Dynamic Floor sets the{' '}
          <em>low edge</em> of that range adaptively, and Rate Stability limits the{' '}
          <em>step size</em> between cycles. The first two are about <strong className="text-foreground">where</strong> a
          rate can be; the third is about <strong className="text-foreground">how quickly it gets there</strong>.
        </p>
      </GuideSection>

      <GuideSection title="Room Bounds: Floor & Ceiling">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each room type has a <strong className="text-foreground">floor</strong> (minimum rate)
          and a <strong className="text-foreground">ceiling</strong> (maximum rate). Clamping
          happens late in the pipeline, after all percentage-based adjustments (seasonal,
          day-of-week, demand, events, gap filling) have been applied.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            Computed <strong className="text-foreground">below the floor</strong> → raised to the
            floor. Engine computes £65, floor is £80, final rate is £80 ("floor applied").
          </li>
          <li>
            Computed <strong className="text-foreground">above the ceiling</strong> → lowered to
            the ceiling. A computed £175 with a £150 ceiling becomes £150.
          </li>
          <li>
            <strong className="text-foreground">Within bounds</strong> → passes through unchanged.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Overrides can exceed every guardrail.</strong> A
          manual override of £160 on a room with a £150 ceiling takes effect (with a warning) —
          overrides are deliberate human decisions and bypass the floor, ceiling, the Dynamic
          Floor, and Rate Stability alike.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Set the <strong className="text-foreground">floor from cost-to-serve</strong> (cleaning,
          linen, staff time, amenities, OTA commission — never lose money on a booking) and the{' '}
          <strong className="text-foreground">ceiling from your competitive set</strong> (the
          highest price a guest would still pick you). At onboarding these are auto-seeded at
          roughly ±30% of the rate in your PMS.
        </p>
      </GuideSection>

      <GuideWidget
        title="Room Bounds in Action"
        footnote="Drag the floor and ceiling to see which rates get clamped."
      >
        <ClampVisualizer />
      </GuideWidget>

      <GuideSection title="Dynamic Floor: An Adaptive Minimum">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The static floor above is a fixed number. The <strong className="text-foreground">Dynamic
          Floor</strong> is a <em>second</em> floor that moves with your performance: it is a
          percentage of your trailing 30-day average rate. If you have been achieving £120 on
          average and the Dynamic Floor is set to 80%, no rate will be published below £96 — even
          if low demand and stacked discounts would push it lower.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Because it is anchored to a rolling average, it self-adjusts: a strong month lifts the
          floor, a soft month relaxes it. The effective minimum each cycle is simply the higher of
          the static room floor and the Dynamic Floor. It has <strong className="text-foreground">no
          memory of yesterday's published price and never touches the upside</strong> — it only
          ever raises a too-low rate.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Enable it on <strong className="text-foreground">Pricing Rules → Price Guardrails</strong>{' '}
          and set the percentage. Use it when you want a level-based revenue safeguard that tracks
          your own recent results rather than a number you have to remember to update.
        </p>
      </GuideSection>

      <GuideSection title="Rate Stability: A Speed Limit on Change">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rate Stability is not a price level at all — it is a <strong className="text-foreground">limit
          on how far the rate can move from the previously published rate in one computation
          cycle</strong>. It has two separate, asymmetric caps:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Max increase per cycle</strong> — a £100 rate with
            a 15% cap can rise to at most £115 this cycle, even if the engine wants £160. The climb
            simply continues over the next cycles.
          </li>
          <li>
            <strong className="text-foreground">Max decrease per cycle</strong> — usually tighter
            (default 5%). A brief demand dip eases the rate down gently instead of collapsing it,
            so a one-off soft day does not trigger a sell-off.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          It runs <strong className="text-foreground">after</strong> the whole pipeline (after
          rounding) and compares against the rate you actually published last cycle. Increases are
          allowed to move faster than decreases by design: capture rising demand quickly, give back
          slowly. Manual overrides bypass it entirely. Turn it on when guests or channel partners
          react badly to volatile pricing and you want a smoother, more defensible rate curve.
        </p>
      </GuideSection>

      <GuideWidget
        title="Rate Stability in Action"
        footnote="Adjust the caps and watch the published line track demand without the whiplash. The dashed line is what the engine computed; the solid line is what guests actually see."
      >
        <RateStabilityVisualizer />
      </GuideWidget>

      <GuideSection title="Floor vs Stability — Don't Confuse Them">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Both can stop a rate from falling, which is why they get mixed up — but they answer
          different questions:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Dynamic Floor asks "is this rate too low?"</strong>{' '}
            — an absolute level, judged against your 30-day average. No memory of the last price.
          </li>
          <li>
            <strong className="text-foreground">Rate Stability asks "did this rate move too far,
            too fast?"</strong> — a change limit, judged against the last published price. It also
            caps the <em>upside</em>, which the Dynamic Floor never does.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Using one alone.</strong> Dynamic Floor only: rates
          can still swing sharply cycle to cycle, but never below your adaptive minimum. Rate
          Stability only: rates move smoothly in both directions, but a sustained slow decline can
          still drift well below your recent average.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Using both together.</strong> They stack cleanly. Each
          cycle the lower bound is whichever is <em>stricter</em> — the Dynamic Floor level, or
          "last published minus the decrease cap" — while Rate Stability additionally caps how fast
          the rate climbs. This is the most protective configuration: a smooth curve that also
          refuses to drift below what your performance justifies. There is no conflict because one
          governs the level and the other governs the step size.
        </p>
      </GuideSection>

      <div className="space-y-3">
        <GuideTip type="do">
          Set the static floor from cost and the ceiling from your competitive set; review both
          quarterly. These are the hard limits everything else lives inside.
        </GuideTip>
        <GuideTip type="do">
          Reach for Rate Stability when channel partners or guests complain about volatile pricing,
          and the Dynamic Floor when you want a revenue safeguard that follows your own results.
          Running both is a sensible default once you trust the engine.
        </GuideTip>
        <GuideTip type="dont">
          Don't use Rate Stability as a price floor. A tight decrease cap slows a decline but never
          stops it — over enough cycles the rate can still reach the static floor. If you need a
          hard low limit, that is the Dynamic Floor's (or the room floor's) job.
        </GuideTip>
        <GuideTip type="dont">
          Don't set the increase cap so tight that the rate can never catch a real demand surge. If
          a sold-out weekend never gets above last week's price, widen the cap or the engine is
          permanently lagging the market.
        </GuideTip>
      </div>

      <GuideRelatedLinks
        links={[
          { slug: 'how-it-works', title: 'How It Works' },
          { slug: 'base-rates', title: 'Base Rates' },
          { slug: 'demand-response', title: 'Demand Response' },
        ]}
      />
    </GuideLayout>
  )
}
